import React, { useState } from 'react';
import { devLog } from '../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useUserAuth } from '../context/UserAuthContext';
import { useLang } from '../context/LangContext';
import { canCancelBid } from '../utils/bidCancelUtils';
import ImageView from '../components/ImageView';
import PrimaryButton from '../components/PrimaryButton';
import FavoriteIcon from '../components/common/FavoriteIcon';
import AuctionHouseComponent from '../components/common/AuctionHouse';
import AdditionalInfoInputModal from '../components/AdditionalInfoInputModal';
import LoginRequiredModal from '../components/LoginRequiredModal';
import { LikedLotData } from '../lib/api/user/userApi.types';
import { isShoppingMode } from '../config/appMode';
import { AppColors } from '../styles/colors';

// Styled Components
const LotCardContainer = styled.div`
  background: white;
  border-radius: 2px;
  overflow: hidden; /* 카드 모서리(border-radius) 밖으로 콘텐츠가 나가지 않도록 */
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  position: relative;

  &:hover {
    transform: translateY(-4px);
  }

  @media (min-width: 768px) {
    display: flex;
    flex-direction: column;
    height: auto;
  }
`;

// 최고/차순위 사전 입찰자 배지 (카드 이미지 하단 오버레이)
const TopPreBidderBadge = styled.div<{ variant?: 'top' | 'second' }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 14px 16px;
  background-color: ${({ variant }) => (variant === 'second' ? AppColors.error : AppColors.btnE)};
  color: ${({ variant }) => (variant === 'second' ? '#ffffff' : AppColors.onBtnE)};
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  box-sizing: border-box;
  opacity: 0.9;
  border-radius: 0 0 2px 2px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// 카드 상단 LOT 정보 (LOT name과 라이브종료 시간)
const CardTopInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin-bottom: 12px;
  gap: 12px;
`;

// 라이브 종료 시간 텍스트
const LiveEndTime = styled.div`
  font-size: 12px;
  color: #999;
  font-weight: 500;
  text-align: right;
  flex-shrink: 0;
`;

// 카드 메인 콘텐츠 (모바일/데스크톱 모두 가로 배치)
const CardMainContent = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-width: 0;
  gap: 16px;
  align-items: stretch;

  @media (min-width: 768px) {
    gap: 24px;
  }
`;

// 이미지 컨테이너
const CardImageContainer = styled.div`
  flex: 1;
  aspect-ratio: 1 / 1; /* 정사각형 비율 유지 */
  position: relative;
  overflow: hidden; /* 이미지가 컨테이너를 꽉 채우도록 잘라냄 */
  border-radius: 2px 0 0 2px;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// 카드 콘텐츠 (모바일/데스크톱 모두 이미지 오른쪽)
const CardContent = styled.div`
  padding: 8px 0px;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  max-width: 100%;
  min-width: 0;
  margin-bottom: 8px; /* 버튼과의 간격 추가 */

  @media (min-width: 768px) {
    padding: 16px;
    margin-bottom: 12px; /* 데스크톱에서는 더 큰 간격 */
  }
`;

// 텍스트 영역
const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
  flex: 1;
`;

const BottomContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
  margin-top: auto;
`;

// 버튼 영역 (카드 하단, 전체 너비)
const ButtonContent = styled.div`
  width: 100%;
  height: 64px; /* 고정 높이 설정 (패딩 + 버튼 높이) */
  box-sizing: border-box;
  margin-top: 16px; /* 이미지+정보 영역과의 간격 추가 */
  display: flex;
  align-items: flex-start;

  button {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }
`;

// 라이브 중 버튼 행 (라이브참여하기 + 라이브입찰하기 버튼을 나란히 배치)
const LiveButtonRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;

  button {
    flex: 1;
    width: auto !important;
    max-width: none !important;
  }

  /* 첫 번째 버튼 (라이브참여하기)의 스타일 변경 */
  button:first-child {
    background-color: #f3f6f6 !important;
    color: #297873 !important;
  }
`;

const AuctionTitle = styled.div`
  font-size: 18px;
  color: #000;
  font-weight: 600;
  margin-bottom: 8px;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
  line-height: 1.4;
  min-height: calc(1.4em * 2);

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const LotName = styled.h3`
  font-size: 16px;
  color: #000;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
  text-align: left;
`;

// LOT ID 스타일 (CardTopInfo에서 사용)
const LotId = styled.h3`
  font-size: 16px;
  color: #000;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
  text-align: left;
`;

// 종료된 경매용 전체 이미지 오버레이
const EndedAuctionOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 2px 2px 2px 2px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EndedAuctionText = styled.span`
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.5px;
`;

// 가격 정보 컨테이너 (배경 없는 column 구조)
const PriceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// 가격 항목 컨테이너
const PriceItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// 가격 레이블
const PriceLabel = styled.span`
  font-size: 12px;
  color: #666666;
  font-weight: 500;
`;

// 가격 값
const PriceValue = styled.span`
  font-size: 14px;
  color: #000;
  font-weight: 600;
`;

// Types
type AuctionStatus = 'before-submission' | 'submission-period' | 'before-live' | 'live' | 'ended';

interface FavoriteCardProps {
  item: LikedLotData;
  auctionStatus: AuctionStatus;
  texts: {
    preSubmission: string;
    liveView: string;
    liveBid: string;
    auctionEnded: string;
    liveStartPrice: string;
    startPrice: string;
    // 경매 종료 시 낙찰가 텍스트 추가
    winningPrice?: string;
    // BidHistory 전용 텍스트들
    cancelBid?: string;
    bidHistory?: string;
  };
  exchangeRate: number;
  formatLiveTime: (liveTime: string, language: 'ko' | 'en') => string;
  formatPrice: (price: number, language: 'ko' | 'en', exchangeRate: number) => string;
  onCardClick: (item: LikedLotData) => void;
  onFavoriteToggle?: (e: React.MouseEvent, index: number) => Promise<void>;
  // BidHistory 관련 props
  isBidHistory?: boolean;
  onCancelBid?: (item: LikedLotData) => void;
  showFavoriteIcon?: boolean;
  // 새로 추가
  formatLiveEndTime?: (dateStr: string, language: 'ko' | 'en') => string;
  // 최고 사전 입찰자 배지 관련
  isTopPreBidder?: boolean;
  topPreBidderLabel?: string;
  // 차순위(2위) 사전 입찰자 배지 관련
  isSecondPreBidder?: boolean;
  secondPreBidderLabel?: string;
}

// Utility Functions
const getImageSrc = (images: string[]) => {
  if (!images || images.length === 0) {
    return "/common/nodata.svg";
  }
  
  const imageUrl = images[0];
  if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
    return imageUrl;
  }
  
  if (imageUrl.startsWith("/")) {
    return `${process.env.REACT_APP_API_HOST}${imageUrl}`;
  }
  
  return `${process.env.REACT_APP_API_HOST}/${imageUrl}`;
};

const LotCard: React.FC<FavoriteCardProps> = ({
  item,
  auctionStatus,
  texts,
  exchangeRate,
  formatLiveTime,
  formatPrice,
  onCardClick,
  onFavoriteToggle,
  isBidHistory = false,
  onCancelBid,
  showFavoriteIcon = true,
  formatLiveEndTime,
  isTopPreBidder = false,
  topPreBidderLabel,
  isSecondPreBidder = false,
  secondPreBidderLabel
}) => {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useUserAuth();
  const { lang } = useLang();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAdditionalInfoInputModal, setShowAdditionalInfoInputModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{ path: string; state?: any } | null>(null);

  // 사용자가 해당 LOT을 좋아요했는지 확인하는 함수
  const isLikedByUser = (likedUserIndexes: number[]) => {
    if (!user?.index || !likedUserIndexes || !Array.isArray(likedUserIndexes)) {
      return false;
    }
    return likedUserIndexes.includes(user.index);
  };

  // 로그인 체크 후 네비게이션 처리 함수
  const handleNavigation = (path: string, state?: any) => {
    // 로그인이 필요한 페이지들 - 기본 경로로 체크
    const loginRequiredPages = ['/bid-request', '/live-bid-request'];
    const isLoginRequiredPage = loginRequiredPages.some(page => path.startsWith(page));
    
    if (!isLoggedIn && isLoginRequiredPage) {
      setShowLoginModal(true);
      return;
    }

    // 로그인은 되어 있지만 추가 정보가 필요한 경우: 추가정보 입력 모달 표시
    if (isLoggedIn && user?.needMoreInfo && isLoginRequiredPage) {
      setPendingNavigation({ path, state });
      setShowAdditionalInfoInputModal(true);
      return;
    }
    
    navigate(path, state ? { state } : undefined);
  };

  // 모달 관련 핸들러들
  const handleCloseModal = () => {
    setShowLoginModal(false);
  };

  const handleLoginFromModal = () => {
    setShowLoginModal(false);
    navigate('/login');
  };

  const handleAdditionalInfoModalClose = () => {
    setShowAdditionalInfoInputModal(false);
    setPendingNavigation(null);
  };

  const handleAdditionalInfoCompleted = () => {
    if (pendingNavigation) {
      navigate(
        pendingNavigation.path,
        pendingNavigation.state ? { state: pendingNavigation.state } : undefined
      );
    }
    setPendingNavigation(null);
  };

  // 버튼 렌더링 함수
  const renderButtons = () => {
    // 경매가 종료된 경우 버튼을 표시하지 않음
    if (auctionStatus === 'ended') {
      return null;
    }

    if (isBidHistory) {
      // BidHistory용 버튼들
      switch (auctionStatus) {
        case 'submission-period':
          const canCancel = canCancelBid(item.auction?.auctionStartAt || '', auctionStatus);
          
          return (
            <LiveButtonRow>
              <PrimaryButton 
                onClick={() => {
                  if (onCancelBid) {
                    onCancelBid(item);
                  }
                }}
              >
                {texts.cancelBid || '입찰취소'}
              </PrimaryButton>
              <PrimaryButton onClick={() => {
                handleNavigation(`/bid-request/${item.index}`, { selectedItem: item });
              }}>
                {texts.preSubmission}
              </PrimaryButton>
            </LiveButtonRow>
          );
        case 'live':
          return (
            <LiveButtonRow>
              <PrimaryButton onClick={() => {
                const transformedItem = {
                  ...item,
                  apiData: {
                    index: item.index,
                    auction: item.auction
                  },
                  auctionIndex: item.auction?.index,
                  lotIndex: item.index,
                  id: item.index,
                  lotId: item.index
                };
                navigate('/live-bid', { state: { selectedItem: transformedItem, auctionIndex: item.auction?.index, lotIndex: item.index } });
              }}>
                {texts.liveView}
              </PrimaryButton>
              <PrimaryButton onClick={() => {
                const transformedItem = {
                  ...item,
                  apiData: {
                    index: item.index,
                    auction: item.auction
                  },
                  auctionIndex: item.auction?.index,
                  lotIndex: item.index,
                  id: item.index,
                  lotId: item.index
                };
                handleNavigation('/live-bid-request', { selectedItem: transformedItem, auctionIndex: item.auction?.index, lotIndex: item.index });
              }}>
                {texts.liveBid}
              </PrimaryButton>
            </LiveButtonRow>
          );
        default:
          return null;
      }
    } else {
      // 기존 Favorites용 버튼들
      switch (auctionStatus) {
        case 'submission-period':
          return (
            <PrimaryButton onClick={() => {
              devLog('🚀 [FavoriteCard] 사전입찰하기/구매하기 클릭:', { item, lotId: item.index, isShoppingMode: isShoppingMode() });
              if (isShoppingMode()) {
                // 쇼핑모드일 때는 ShoppingRequest 페이지로 이동
                navigate(`/shopping-request/${item.index}`);
              } else {
                // 경매모드일 때는 기존 BidRequest 페이지로 이동
                handleNavigation(`/bid-request/${item.index}`, { selectedItem: item });
              }
            }}>
              {texts.preSubmission}
            </PrimaryButton>
          );
        case 'live':
          if (isShoppingMode()) {
            // 쇼핑모드일 때는 구매하기 버튼 하나만 표시
            return (
              <PrimaryButton onClick={() => {
                devLog('🚀 [FavoriteCard] 쇼핑모드 구매하기 클릭:', { item, lotId: item.index });
                navigate(`/shopping-request/${item.index}`);
              }}>
                {texts.liveBid}
              </PrimaryButton>
            );
          } else {
            // 경매모드일 때는 기존 라이브 버튼들 표시
            return (
              <LiveButtonRow>
                <PrimaryButton onClick={() => {
                  const transformedItem = {
                    ...item,
                    apiData: {
                      index: item.index,
                      auction: item.auction
                    },
                    auctionIndex: item.auction?.index,
                    lotIndex: item.index,
                    id: item.index,
                    lotId: item.index
                  };
                  navigate('/live-bid', { state: { selectedItem: transformedItem, auctionIndex: item.auction?.index, lotIndex: item.index } });
                }}>
                  {texts.liveView}
                </PrimaryButton>
                <PrimaryButton onClick={() => {
                  const transformedItem = {
                    ...item,
                    apiData: {
                      index: item.index,
                      auction: item.auction
                    },
                    auctionIndex: item.auction?.index,
                    lotIndex: item.index,
                    id: item.index,
                    lotId: item.index
                  };
                  handleNavigation('/live-bid-request', { selectedItem: transformedItem, auctionIndex: item.auction?.index, lotIndex: item.index });
                }}>
                  {texts.liveBid}
                </PrimaryButton>
              </LiveButtonRow>
            );
          }
        default:
          return null;
      }
    }
  };

  // 버튼 표시 여부 결정 - 경매 종료 시에는 버튼을 표시하지 않음
  const showButton = auctionStatus === 'submission-period' || auctionStatus === 'live';

  return (
    <>
      <LotCardContainer onClick={() => {
        if (isShoppingMode()) {
          // 쇼핑모드일 때는 ShoppingRequest 페이지로 이동
          navigate(`/shopping-request/${item.index}`);
        } else {
          // 경매모드일 때는 기존 onCardClick 핸들러 사용
          onCardClick(item);
        }
      }}>
        {/* 카드 상단: LOT ID와 라이브종료 시간을 row space-between으로 배치 (쇼핑 모드에서는 숨김) */}
        {!isShoppingMode() && (
          <CardTopInfo>
            <LotId>{item.lotCode || item.code || `LOT ${item.index}`}</LotId>
            {formatLiveEndTime && item.auction?.auctionEndAt && (
              <LiveEndTime>{formatLiveEndTime(item.auction.auctionEndAt, lang)}</LiveEndTime>
            )}
          </CardTopInfo>
        )}
        
        <CardMainContent>
          <CardImageContainer>
            <ImageView
              imageUrl={getImageSrc(item.images)}
              width="100%"
              height="100%"
              borderRadius={2}
              alt={item.name}
              objectFit="cover"
            />

              {/* 최고/차순위 사전 입찰자 배지 - 카드 이미지 하단 오버레이 */}
              {auctionStatus !== 'ended' && (
                <>
                  {isTopPreBidder && (
                    <TopPreBidderBadge variant="top">
                      {topPreBidderLabel || '최고가 입찰'}
                    </TopPreBidderBadge>
                  )}
                  {!isTopPreBidder && isSecondPreBidder && (
                    <TopPreBidderBadge variant="second">
                      {secondPreBidderLabel || '경쟁 최고가 발생'}
                    </TopPreBidderBadge>
                  )}
                </>
              )}
            
            {/* 경매 종료 오버레이 */}
            {auctionStatus === 'ended' && (
              <EndedAuctionOverlay>
                <EndedAuctionText>{texts.auctionEnded}</EndedAuctionText>
              </EndedAuctionOverlay>
            )}
            
            {/* 즐겨찾기 아이콘 - 항상 표시 */}
            {showFavoriteIcon && (
              <FavoriteIcon
                isFavorite={isLikedByUser(item.likedUserIndexes)}
                onClick={onFavoriteToggle ? (e) => onFavoriteToggle(e, item.index) : undefined}
              />
            )}
          </CardImageContainer>
          
          <CardContent>
            <TextContent>
              <AuctionHouseComponent 
                auctioneerName={item.auctioneer?.name || 'BidSpace'}
                auctioneerIndex={item.auctioneer?.index || 0}
              />
              <AuctionTitle>{item.name || item.auction?.name}</AuctionTitle>
            </TextContent>

            <BottomContent>
              {!isShoppingMode() && (
                <LotName>{item.lotCode || item.code || `LOT ${item.index}`}</LotName>
              )}

              {/* 가격 정보 표시 - 경매 상태에 따라 분기 */}
              <PriceInfo>
                {auctionStatus === 'ended' ? (
                  // 경매 종료 시: 시작가 + 낙찰가
                  <>
                    <PriceItem>
                      <PriceLabel>{texts.startPrice}</PriceLabel>
                      <PriceValue>{formatPrice((item as any).originalStartPrice || item.startPrice || 0, lang, exchangeRate)}</PriceValue>
                    </PriceItem>
                    {texts.winningPrice && (item as any).apiData?.highestBid && (
                      <PriceItem>
                        <PriceLabel>{texts.winningPrice}</PriceLabel>
                        <PriceValue>{formatPrice((item as any).apiData.highestBid || 0, lang, exchangeRate)}</PriceValue>
                      </PriceItem>
                    )}
                  </>
                ) : (
                  // 경매 진행 중: 라이브 시작가 + 시작가
                  <>
                    {!isShoppingMode() && texts.liveStartPrice && (
                      <PriceItem>
                        <PriceLabel>{texts.liveStartPrice}</PriceLabel>
                        <PriceValue>{formatPrice(item.startPrice || 0, lang, exchangeRate)}</PriceValue>
                      </PriceItem>
                    )}
                    {texts.startPrice && (
                      <PriceItem>
                        <PriceLabel>{texts.startPrice}</PriceLabel>
                        <PriceValue>{formatPrice((item as any).originalStartPrice || item.startPrice || 0, lang, exchangeRate)}</PriceValue>
                      </PriceItem>
                    )}
                  </>
                )}
              </PriceInfo>
            </BottomContent>
          </CardContent>
        </CardMainContent>
        
        {/* 버튼 영역은 항상 렌더링하되, 조건에 따라 내용만 표시/숨김 */}
        <ButtonContent onClick={(e) => e.stopPropagation()}>
          {showButton && renderButtons()}
        </ButtonContent>
      </LotCardContainer>
      
      {/* 로그인 모달 */}
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={handleCloseModal}
        onLogin={handleLoginFromModal}
      />

      {/* 추가 정보 입력 모달 (사전입찰/라이브입찰 공통) */}
      <AdditionalInfoInputModal
        isOpen={showAdditionalInfoInputModal}
        onClose={handleAdditionalInfoModalClose}
        onCompleted={handleAdditionalInfoCompleted}
      />
    </>
  );
};

export default LotCard;
